import { useRef } from "react";
import FormFlows, { type FormFlowGridRef } from "./formFlow";
import { defaultModulesMapper } from "@Helpers";
import { getEntitesFromLibraryUrl } from "@Services/dataManager";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import { useTranslation } from "@i18n";
import { getUserSelection } from "../CreateInspectionAndProgress/formsHelpers";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";
import useFormFlowLibrary from "./useFormFlowLibrary";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { AddFromLibraryState, FormFlowRow } from "./formFlow.types";

interface FormFlowLibraryPopupProps {
  projectId?: string;
  /** Closes the wizard. */
  handleCancelibrary: () => void;
  setAddFromLibrary: (updater: (prev: AddFromLibraryState) => AddFromLibraryState) => void;
  addFromLibrary: AddFromLibraryState;
  /** Form category whose library entries are listed. */
  resourceType: string;
  /** Re-fetch the project's flows after a successful copy. */
  refreshGrid: () => void;
}

const FormFlowLibraryPopup = ({
  projectId,
  handleCancelibrary,
  setAddFromLibrary,
  addFromLibrary,
  resourceType,
  refreshGrid,
}: FormFlowLibraryPopupProps) => {
  const { t } = useTranslation();
  const gridRef = useRef<FormFlowGridRef | null>(null);

  const {
    customQuery,
    libraryService,
    isSelectionStep,
    handleNextClick,
    handleBackToSelection,
    isNextDisabled,
    setSpaceId,
    setNextBtnState,
    userSelectionMode,
    handleUserSelectionModeChange,
    handleCreateFromLibrary,
  } = useFormFlowLibrary({
    projectId,
    resourceType,
    libraryServiceUrl: getEntitesFromLibraryUrl,
    addFromLibrary,
    setAddFromLibrary,
    handleCancelibrary,
    refreshGrid,
    getSelectedRows: () =>
      gridRef.current?.slickGrid
        .getSelectedRows()
        .map((row: number) => gridRef.current!.slickGrid.getDataItem(row) as FormFlowRow) ?? [],
  });

  const content = () => {
    if (isSelectionStep) {
      return (
        <FormFlows
          ref={gridRef}
          setNextBtnState={setNextBtnState}
          customService={libraryService}
          showOnlyGrid={true}
          customQuery={customQuery}
        />
      );
    }
    return getUserSelection(
      "Choose who you want to assign in the Form Flow.",
      userSelectionMode,
      handleUserSelectionModeChange,
    );
  };

  const header = () => (
    <div className="flex w-full items-center">
      <div className="flex justify-between w-full items-center">
        <div className="items-center flex gap-1">
          {!isSelectionStep && (
            <IconComponent
              onClick={handleBackToSelection}
              name="BackwardArrow"
              width="1rem"
              height="1rem"
              className="cursor-pointer"
            />
          )}
          {t("Create from Library")}
        </div>
        {isSelectionStep && (
          <SelectSpaces
            moduleName={defaultModulesMapper.CONTRACTS}
            data-testid="spaceDropdownField"
            projectId={projectId}
            selectedSpaceCallback={(params: { _id: ObjectIdString }) => {
              setSpaceId(params._id);
            }}
          />
        )}
      </div>
    </div>
  );

  const footer = () => {
    if (isSelectionStep) {
      return (
        <div className="flex w-full items-center gap-5">
          <ButtonComponent
            disabled={isNextDisabled}
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={handleNextClick}
          >
            {t("Next")}
          </ButtonComponent>
          <ButtonComponent onClick={handleCancelibrary}>{t("Cancel")}</ButtonComponent>
        </div>
      );
    }
    return (
      <div className="flex w-full pt-5 items-center gap-5">
        <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleCreateFromLibrary}>
          {t("Create")}
        </ButtonComponent>
        <ButtonComponent onClick={handleCancelibrary}>{t("Cancel")}</ButtonComponent>
      </div>
    );
  };

  return (
    <ModalStateDialogComponent
      allowModalPadding={true}
      height="70%"
      footerContent={footer()}
      width="80%"
      headerTitle={header()}
      onCancel={handleCancelibrary}
      isVisible={true}
      modelContent={content()}
    />
  );
};
export default FormFlowLibraryPopup;
